import {
  faArrowLeft,
  faClock,
  faPrint,
  faRepeatAlt,
  faTruckMoving,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button as MantineButton, Group } from '@mantine/core'
import lodash, { first, get, map, noop } from 'lodash'
import moment from 'moment'
import React, { ReactElement, useEffect, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'

import Address, {
  unprefixAddress,
} from '../../../1stlog-ui/src/components/Address'
import Alert from '../../../1stlog-ui/src/components/Alert'
import Card from '../../../1stlog-ui/src/components/Card'
import CronEditor from '../../../1stlog-ui/src/components/CronEditor'
import Form from '../../../1stlog-ui/src/components/Form'
import Link from '../../../1stlog-ui/src/components/Link'
import LoaderOverlay from '../../../1stlog-ui/src/components/LoaderOverlay'
import AdditionalServicesTable from '../../../1stlog-ui/src/components/OrderDetails/AdditionalServicesTable'
import ColiTable from '../../../1stlog-ui/src/components/OrderDetails/ColiTable'
import { TOrder } from '../../../1stlog-ui/src/components/OrderDetails/helpers'
import PrintFormatSelect from '../../../shipmetrics-shared/components/PrintFormatSelect'
import { Wrapper } from '../../../shipmetrics-shared/components/ShipmentDetails/AddressBar'
import ShipmentOverview from '../../../shipmetrics-shared/containers/OrderEntry/Form/Shipment/Overview'
import { OrderEntryContextProvider } from '../../../shipmetrics-shared/containers/OrderEntry/OrderEntryContext'
import { TOrderEntryExternalValues } from '../../../shipmetrics-shared/containers/OrderEntry/types'
import externalValuesToOrderEntryValues from '../../../shipmetrics-shared/containers/OrderEntry/utils/externalValuesToOrderEntryValues'
import { useCustomer } from '../../../src/components/Customer'
import TemplateEditorButton from '../../containers/Templates/TemplateEditor/TemplateEditorButton'
import useTemplateValues from '../../containers/Templates/useTemplateValues'
import { useInlineTrackingStore } from '../InlineTracking/useInlineTrackingStore'
import { getTrackingLink } from '../TrackingLink/getTrackingLink'
import Colis from './Colis'
import Customs from './Customs'
import Details from './Details'
import DocumentTable from './DocumentTable'
import makeFormValues from './makeFormValues'
import ShipmentServices from './ShipmentServices'
import useOrderDetailV2 from './useOrderDetailV2'

type TOrderDetailProps = {
  item: TOrder // TOrderDetail
  isLoading: boolean
  cancelColi: () => void
  error: any
  requestOrder: any
  hideSimiliarOrder: boolean
  saveValues: (values: any) => void
  match: any
  history: any
  location: any
}

const OrderDetail = (props: TOrderDetailProps): ReactElement => {
  const {
    item = undefined,
    isLoading,
    cancelColi,
    saveValues,
    requestOrder,
    hideSimiliarOrder,
    error,
  } = props

  const showInlineTracking = /(localhost|local|staging)/.test(
    window.location.href,
  )

  useEffect(() => {
    requestOrder(props.match.params.id)
  }, [])

  const { currentCustomer } = useCustomer()

  const [localLoading, setLocalLoading] = useState(false)

  // console.log('item', item)

  const { setTemplateValues } = useTemplateValues()

  const Outer = ({ children: outerPropsChildren }): ReactElement => (
    <Container className="mt-4">
      <LoaderOverlay fullsize isLoading={isLoading || localLoading} />
      {error && (
        <Alert danger title={i18n.t('error', 'Fehler')}>
          {i18n.t(
            'error_fetching_order',
            ` Der Auftrag konnte nicht abgefragt werden. Bitte versuchen Sie es
          später erneut.`,
          )}
        </Alert>
      )}
      {outerPropsChildren}
    </Container>
  )

  const { checkboxes, showNew, showOld } = useOrderDetailV2()

  const { open, isActive: isInlineTrackingActive } = useInlineTrackingStore()

  if (!item) {
    return (
      <Outer>
        <></>
      </Outer>
    )
  }

  const newOrder = (): void => {
    const orderValues: TOrderEntryExternalValues = {
      pickupAddress: {
        name1: item.senderName1,
        name2: item.senderName2 ?? undefined,
        name3: item.senderName3 ?? undefined,
        street: item.senderStreet,
        streetNumber: item.senderStreetNumber,
        streetNumberAddition: item.senderStreetNumberAddition ?? undefined,
        zip: item.senderZip,
        city: item.senderCity,
        country: item.senderCountry,
        prename: item.senderPrename ?? undefined,
        surname: item.senderSurname ?? undefined,
        phone1: item.senderPhone1 ?? undefined,
        mail: item.senderMail ?? undefined,
        eori: item.senderEORI ?? undefined,
        ustid: item.senderUSTID ?? undefined,
      },
      deliveryAddress: {
        name1: item.receiverName1,
        name2: item.receiverName2 ?? undefined,
        name3: item.receiverName3 ?? undefined,
        street: item.receiverStreet,
        streetNumber: item.receiverStreetNumber ?? undefined,
        streetNumberAddition: item.receiverStreetNumberAddition ?? undefined,
        zip: item.receiverZip,
        city: item.receiverCity,
        country: item.receiverCountry,
        prename: item.receiverPrename ?? undefined,
        surname: item.receiverSurname ?? undefined,
        phone1: item.receiverPhone1 ?? undefined,
        mail: item.receiverMail ?? undefined,
        eori: item.receiverEORI ?? undefined,
        ustid: item.receiverUSTID ?? undefined,
      },
      slaType: item.sla_object.name,
    }

    const url = '/'
    // saveValues(orderValues)
    setTemplateValues(externalValuesToOrderEntryValues(orderValues, item))

    props.history.push(url)
  }

  const firstColi = first(item.colis)

  const senderAddress = unprefixAddress(
    {
      ...item,
      senderEori: item.senderEORI ?? undefined,
      senderUstid: item.senderUSTID,
      senderAddressHint: firstColi?.senderCustomerHint,
    },
    'sender',
  )

  const invoiceAddress = unprefixAddress(
    { ...item, invoiceEori: item.invoiceEORI, invoiceUstid: item.invoiceUSTID },
    'invoice',
  )

  const receiverAddress = unprefixAddress(
    {
      ...item,
      receiverEori: item.receiverEORI,
      receiverUstid: item.receiverUSTID,
      receiverAddressHint: firstColi?.receiverCustomerHint,
    },
    'receiver',
  )

  const neutralAddress = unprefixAddress(
    {
      ...item,
      neutralEori: item.neutralSenderEORI,
      neutralUstid: item.neutralSenderUSTID,
    },
    'neutral',
  )

  const formValues = makeFormValues(item)
  // console.log('formValues', formValues)

  const openTrackingPopup = (): void => {
    if (!isInlineTrackingActive) return
    open({
      barcode: String(first(item.colis)?.barcode1),
      zip: item.receiverZip,
    })
  }

  return (
    <Outer>
      {checkboxes}
      <OrderEntryContextProvider
        key="OrderDetail"
        value={{
          isOrderEditor: true,
          id: 'OrderDetail',
          addressbookUrl: '',
          barcodeUrl: '',
          customsUrl: '',
          hazmatUrl: '',
          reusableBoxUrl: '',
          providers: [],
          requestSubmit: async () => {
            //
          },
          initialValues: formValues,
          preset: {},
          constantOrderEntryProduct: {
            allowedFreightTermList: get(global, 'allowedFreightTerms', []),
            allowedPackagingList: get(global, 'allowedPackaging', []),
            isFreightTermsAllowed: true,
            isPackagingAllowed: true,
            isContentDescriptionAllowed: true,
            isvalueOfGoodsAllowed: !!item.valueOfGoods,
            showVolumeWeight: true,
            volumeWeightFactor: 5000,
          },
        }}
      >
        <Form
          initialValues={formValues}
          onSubmit={noop}
          showFormStateWindowCheckbox
          onlyMountWhenValuesAreSet
        >
          <>
            <div>
              <Link href="/orders">
                <MantineButton
                  variant="subtle"
                  leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
                >
                  {i18n.t('btn_back', 'Zurück')}
                </MantineButton>
              </Link>
            </div>

            <Card noHeader>
              <Row>
                <Col className="">
                  <Group position="apart" spacing="xl" noWrap align="start">
                    <div>
                      <Group spacing="xs" style={{ justifyContent: 'end' }}>
                        {item.printAllowed && (
                          <div>
                            <PrintFormatSelect
                              labelToken={item.id}
                              url="/api/orderlisting/print/"
                            />
                          </div>
                        )}

                        {item.proformaInvoiceAvailable ? (
                          <div>
                            <Link
                              href={`/auftrag/api/order/proforma/byid/${item.id}`}
                              target="_blank"
                            >
                              <MantineButton
                                variant="light"
                                color="blue"
                                leftIcon={<FontAwesomeIcon icon={faPrint} />}
                              >
                                {i18n.t(
                                  'print_proforma_invoice',
                                  'Proforma-Rechnung drucken',
                                )}
                              </MantineButton>
                            </Link>
                          </div>
                        ) : null}

                        {!hideSimiliarOrder && (
                          <MantineButton
                            // variant="light"
                            leftIcon={<FontAwesomeIcon icon={faRepeatAlt} />}
                            onClick={() => newOrder()}
                          >
                            {i18n.t(
                              'create_similar_order',
                              'Ähnlichen Auftrag erfassen',
                            )}
                          </MantineButton>
                        )}

                        <div>
                          <TemplateEditorButton values={item} />
                        </div>

                        {lodash.get(
                          currentCustomer,
                          'cronOrdersEnabled',
                          false,
                        ) && (
                          <CronEditor
                            order={item}
                            setLoading={setLocalLoading}
                            redirectUrl="/cronorders"
                            history={props.history}
                            location={props.location}
                            match={props.match}
                            buttonElement={
                              <MantineButton
                                // variant="light"
                                leftIcon={<FontAwesomeIcon icon={faClock} />}
                              >
                                {i18n.t('cron_order', 'Dauerauftrag')}
                              </MantineButton>
                            }
                          />
                        )}
                        {isInlineTrackingActive ? (
                          <MantineButton
                            // variant="light"
                            leftIcon={<FontAwesomeIcon icon={faTruckMoving} />}
                            onClick={() => openTrackingPopup()}
                          >
                            {i18n.t('track_and_trace', 'Track & Trace')}
                          </MantineButton>
                        ) : (
                          <Link
                            target="_blank"
                            href={getTrackingLink(
                              get(item.colis[0], 'barcode1'),
                              item.trackingHash,
                            )}
                          >
                            <MantineButton
                              // variant="light"
                              leftIcon={
                                <FontAwesomeIcon icon={faTruckMoving} />
                              }
                            >
                              {i18n.t('track_and_trace', 'Track & Trace')}
                            </MantineButton>
                          </Link>
                        )}
                      </Group>
                    </div>
                  </Group>

                  {/* </Col>
              <Col
                md="9"
                className="text-right d-flex align-items-center justify-content-end"
              > */}
                </Col>
              </Row>
            </Card>
            <Card noHeader customContentPaddingClass="px-2 pt-2" style={{}}>
              <>
                {/* <Status order={item} /> */}
                <Row>
                  <Col md="6">
                    <h3 className="mb-0">{`${i18n.t('order', 'Auftrag')} #${
                      item.id
                    }`}</h3>
                    <div className="text-muted">
                      {i18n.t('created_at_x_by_y', {
                        defaultValue: 'Erfasst am {{date}} von {{name}}',
                        date: moment(item.created_at).format(
                          'DD.MM.YYYY HH:mm',
                        ),
                        name: item.created_by_name || 'Unbekannt',
                      })}
                    </div>
                  </Col>
                  <Col md="6" className="text-right">
                    <h3 className="mb-0">{`${lodash.get(
                      item,
                      'sla',
                      i18n.t('unknown', 'Unbekannt'),
                    )}`}</h3>
                    <div className="text-muted">
                      {item?.sla_object?.description}
                    </div>
                  </Col>
                </Row>

                <Row className="mt-2">
                  <Col md="12">
                    <div style={{ margin: '0 -12px' }}>
                      <Wrapper>
                        <div className="mx-3">
                          <Group
                            position="apart"
                            style={{ alignItems: 'start' }}
                          >
                            <div>
                              <h4>{i18n.t('sender', 'Versender')}</h4>
                              <Address {...senderAddress} />
                            </div>

                            <div>
                              <h4>
                                {i18n.t('billing_address', 'Rechnungsadresse')}
                              </h4>
                              <Address {...invoiceAddress} />
                            </div>

                            <div>
                              <h4>{i18n.t('recipient', 'Empfänger')}</h4>
                              <Address {...receiverAddress} />
                            </div>

                            {lodash.includes(
                              map(item.additional_services, (s) => s.name),
                              'NSH',
                            ) && (
                              <div>
                                <h4>
                                  {i18n.t('neutral_sender', 'Neutralversender')}
                                </h4>
                                <Address {...neutralAddress} />
                              </div>
                            )}
                          </Group>
                        </div>
                      </Wrapper>
                    </div>
                  </Col>
                </Row>

                {showNew && (
                  <div className="mt-2">
                    <ShipmentOverview readonly showRoutingInformation />
                  </div>
                )}

                {(lodash.size(item.additional_services) > 0 ||
                  item.cashondelivery) && (
                  <div className="mt-2">
                    {showNew && <ShipmentServices />}
                    {showOld && <AdditionalServicesTable order={item} />}
                  </div>
                )}

                {showNew && <Colis cancelColi={cancelColi} order={item} />}

                {showNew && <Customs />}

                {showOld && <Details order={item} />}

                {showOld && (
                  <Row className="mt-2">
                    <Col>
                      <h4>{i18n.t('colis_plural', 'Colis')}</h4>
                    </Col>
                    <Col md="12">
                      <ColiTable order={item} cancelColi={cancelColi} />
                    </Col>
                  </Row>
                )}

                <Row className="">
                  <Col md="12">
                    <DocumentTable order={item} />
                  </Col>
                </Row>
              </>
            </Card>
          </>
        </Form>
      </OrderEntryContextProvider>
    </Outer>
  )
}

export default OrderDetail
